import { useMemo } from 'react'

import { Chip, DescriptionList, SkeletonBox } from '@circlefin/components'
import { useDate, useMoney } from '@circlefin/hooks'
import { Currency, TradeStatus } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import type { Trade, TradeTransactionType } from '@shared/graphql'

export interface TradeCardProps {
  /**
   * Trade object containing information about the trade.
   */
  trade?: Trade
  /**
   * Trade type. Can either be trade_mint or trade_withdrawal.
   */
  tradeType: TradeTransactionType
  /**
   * Display loading state.
   */
  loading?: boolean
}

export const TradeCard: React.FC<TradeCardProps> = ({
  trade,
  tradeType,
  loading,
}) => {
  const { t } = useTranslation('payments')
  const { money } = useMoney()
  const { date: formatDate } = useDate()
  const from = trade?.from
  const fromAmount = useMemo(
    () =>
      from?.amount
        ? money({
            number: from.amount,
            variant: from.currency ?? Currency.USD,
            options: {
              symbol: true,
              signDisplay: false,
            },
          })
        : t('tradeCard.waitingForFx'),
    [from, money, t],
  )
  const to = trade?.to
  const toAmount = useMemo(
    () =>
      to?.amount
        ? money({
            number: to.amount,
            variant: to.currency ?? Currency.USD,
            options: {
              symbol: true,
              signDisplay: false,
            },
          })
        : t('tradeCard.waitingForFx'),
    [money, t, to],
  )

  const statusVariant = useMemo(() => {
    switch (trade?.status) {
      case TradeStatus.complete:
        return 'status/success'
      case TradeStatus.failed:
        return 'status/error'
      case TradeStatus.confirmed:
      case TradeStatus.pending:
      case TradeStatus.pending_settlement:
      case undefined:
        return 'status/neutral'
    }
  }, [trade?.status])

  const tradeTypeString = useMemo(() => {
    return t(`tradeCard.${tradeType}`)
  }, [t, tradeType])

  if (loading) {
    return (
      <div className="space-y-3">
        <SkeletonBox className="h-4 w-88" loading={loading} />
        <SkeletonBox className="h-4 w-120" loading={loading} />
        <SkeletonBox className="h-4 w-48" loading={loading} />
      </div>
    )
  }

  if (!trade) {
    return null
  }

  return (
    <>
      <h2 className="type-h-title-md">{t('tradeCard.details')}</h2>
      <DescriptionList density="loose">
        <DescriptionList.Label className="w-64">
          {t('tradeCard.type')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <p className="type-body-base-bold">{tradeTypeString}</p>
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('tradeCard.tradeId')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <p className="type-body-base-bold">{trade.id}</p>
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('tradeCard.quoteId')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <p className="type-body-base-bold">{trade?.quoteId ?? ''}</p>
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('tradeCard.settlementId')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <p className="type-body-base-bold">{trade?.settlementId ?? ''}</p>
        </DescriptionList.Description>

        <DescriptionList.Label className="h-full w-64">
          {t('tradeCard.status')}
        </DescriptionList.Label>
        <DescriptionList.Description className="flex flex-col">
          <Chip variant={statusVariant}>
            <p className="capitalize type-body-base-bold">{trade?.status}</p>
          </Chip>
          <p className="type-body-xs">
            {formatDate(trade?.updateDate ?? '', 'dateTimeWithYear')}
          </p>
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('tradeCard.fromAmount')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <p className="type-body-base-bold">{fromAmount}</p>
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('tradeCard.toAmount')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <p className="type-body-base-bold">{toAmount}</p>
        </DescriptionList.Description>
      </DescriptionList>
    </>
  )
}
